import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/use-toast';
import { Loader2, Mail, Send } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../utils/api-client';

export default function ContactPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    description: ''
  });

  // Check if all required fields are filled
  const isFormValid = () => {
    if (isAuthenticated) {
      // If logged in, only subject and description are required
      return formData.subject.trim() !== '' && formData.description.trim() !== '';
    } else {
      // If not logged in, all fields are required
      return (
        formData.name.trim() !== '' &&
        formData.email.trim() !== '' &&
        formData.subject.trim() !== '' &&
        formData.description.trim() !== ''
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      toast({
        title: t('contact.error'),
        description: t('contact.fillAllFields'),
        variant: 'destructive',
      });
      return;
    }

    setSending(true);

    try {
      const response = await apiClient.post('/api/contact', {
        name: isAuthenticated ? (user?.name || '') : formData.name,
        email: isAuthenticated ? (user?.email || '') : formData.email,
        subject: formData.subject,
        description: formData.description,
      });

      if (response.ok) {
        toast({
          title: t('contact.success'),
          description: t('contact.successMessage'),
          duration: 5000,
        });

        // Reset form
        setFormData({
          name: user?.name || '',
          email: user?.email || '',
          subject: '',
          description: ''
        });

        // Redirect to home page after showing success message
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: t('contact.error'),
          description: errorData.error || t('contact.errorMessage'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: t('contact.error'),
        description: t('contact.errorMessage'),
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-6 w-6" />
            {t('contact.title')}
          </CardTitle>
          <CardDescription>
            {t('contact.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isAuthenticated && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">{t('contact.name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder={t('contact.namePlaceholder')}
                    required={!isAuthenticated}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('contact.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder={t('contact.emailPlaceholder')}
                    required={!isAuthenticated}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="subject">{t('contact.subject')}</Label>
              <Input
                id="subject"
                type="text"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder={t('contact.subjectPlaceholder')}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('contact.description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('contact.descriptionPlaceholder')}
                rows={6}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={!isFormValid() || sending}
              className="w-full"
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('contact.sending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('contact.send')}
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
